/*
  Tuki Taki product page behaviour.
  No dependencies. Everything hangs off the tt-product and tt-recommendations
  custom elements plus a few delegated listeners, so it also works when the
  theme editor re-renders a section.
*/
(() => {
  if (window.__ttPdpLoaded) return;
  window.__ttPdpLoaded = true;

  /* ---------- Helpers ---------- */

  // Formats cents with the shop's money format and drops trailing ".00",
  // matching Liquid's money_without_trailing_zeros filter.
  const formatMoney = (cents, format) => {
    const group = (amount, decimals, thousands, decimal) => {
      const [whole, fraction] = (amount / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction && Number(fraction) > 0 ? grouped + decimal + fraction : grouped;
    };
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const template = pattern.test(format || '') ? format : '{{amount}}';
    return template.replace(pattern, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return group(cents, 0, ' ', '.');
        case 'amount_with_space_separator':
          return group(cents, 2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return group(cents, 2, ' ', '.');
        default:
          return group(cents, 2, ',', '.');
      }
    });
  };

  // Adds to cart through the Ajax API. If the theme has a Dawn-style cart
  // drawer or cart notification, it is refreshed and opened.
  const addToCart = async (formData, addUrl) => {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const canRender =
      !!cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function';

    if (canRender) {
      formData.append(
        'sections',
        cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',')
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    const response = await fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: formData,
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      const message = [data.description, data.message].find((text) => typeof text === 'string' && text);
      const error = new Error(message || 'Sorry, this could not be added to your cart.');
      error.fromCart = true;
      throw error;
    }

    if (canRender) {
      cart.classList.remove('is-empty');
      cart.renderContents(data);
    }
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'tt-product-form',
        productVariantId: formData.get('id'),
        cartData: data,
      });
    }
    document.dispatchEvent(new CustomEvent('tt:cart:added', { detail: { item: data, openedCart: canRender } }));
    return { data, openedCart: canRender };
  };

  /* ---------- Product ---------- */

  class TTProduct extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      const json = this.querySelector('[data-tt-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.form = this.querySelector('form[data-tt-form]');
      this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.variant = this.idInput
        ? this.variants.find((variant) => String(variant.id) === String(this.idInput.value)) || null
        : null;

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-tt-option]')) this.onOptionChange();
        if (event.target.matches('[data-tt-qty-input]')) this.onQuantityChange(0);
      });
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-tt-qty]');
        if (step) this.onQuantityChange(Number(step.dataset.ttQty));

        const thumb = event.target.closest('[data-tt-thumb]');
        if (thumb) this.goToSlide(this.thumbs.indexOf(thumb));

        const nav = event.target.closest('[data-tt-gallery-nav]');
        if (nav) this.goToSlide(this.activeIndex + Number(nav.dataset.ttGalleryNav));
      });
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

      this.initGallery();
      this.updateSoldOutPills();
      this.updateTotal();
      this.updateDelivery();

      if (this.variant && this.variant.featured_media) this.showMedia(this.variant.featured_media.id, false);
    }

    /* Gallery */

    initGallery() {
      this.track = this.querySelector('[data-tt-track]');
      this.slides = this.track ? Array.from(this.track.querySelectorAll('[data-tt-slide]')) : [];
      this.thumbs = Array.from(this.querySelectorAll('[data-tt-thumb]'));
      this.activeIndex = 0;
      if (!this.track) return;

      let frame;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const width = this.track.clientWidth || 1;
            this.setActiveSlide(Math.round(this.track.scrollLeft / width));
          });
        },
        { passive: true }
      );
      this.setActiveSlide(0);
    }

    goToSlide(index, smooth = true) {
      if (!this.track || !this.slides.length) return;
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      this.setActiveSlide(target);
    }

    setActiveSlide(index) {
      if (!this.slides.length) return;
      const active = Math.max(0, Math.min(this.slides.length - 1, index));
      const changed = active !== this.activeIndex;
      this.activeIndex = active;

      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === active ? 'true' : 'false'));
      const count = this.querySelector('[data-tt-count]');
      if (count) count.textContent = `${active + 1} / ${this.slides.length}`;
      this.querySelectorAll('[data-tt-gallery-nav]').forEach((button) => {
        const direction = Number(button.dataset.ttGalleryNav);
        button.disabled = direction < 0 ? active === 0 : active === this.slides.length - 1;
      });

      if (changed) {
        this.slides.forEach((slide, i) => {
          if (i !== active) slide.querySelectorAll('video').forEach((video) => video.pause());
        });
        const thumb = this.thumbs[active];
        if (thumb && thumb.parentElement.scrollHeight > thumb.parentElement.clientHeight) {
          thumb.parentElement.scrollTo({ top: thumb.offsetTop - 60, behavior: 'smooth' });
        }
      }
    }

    showMedia(mediaId, smooth = true) {
      const index = this.slides.findIndex((slide) => slide.dataset.ttSlide === String(mediaId));
      if (index > -1) this.goToSlide(index, smooth);
    }

    /* Variants */

    get optionGroups() {
      return Array.from(this.querySelectorAll('[data-tt-option-group]'));
    }

    selectedOptions() {
      return this.optionGroups.map((group) => {
        const checked = group.querySelector('[data-tt-option]:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      this.optionGroups.forEach((group, i) => {
        const output = group.querySelector('[data-tt-option-value]');
        if (output) output.textContent = selected[i] || '';
      });

      const variant = this.variants.find((candidate) =>
        candidate.options.every((value, i) => value === selected[i])
      );
      this.setVariant(variant || null);
      this.updateSoldOutPills();
    }

    setVariant(variant) {
      this.variant = variant;
      const add = this.querySelector('[data-tt-add]');
      const label = this.querySelector('[data-tt-add-label]');
      const dynamic = this.querySelector('[data-tt-dynamic]');
      const total = this.querySelector('[data-tt-total]');
      this.setStatus('');

      if (!variant) {
        if (add) add.disabled = true;
        if (label && add) label.textContent = add.dataset.labelUnavailable;
        if (total) total.hidden = true;
        if (dynamic) dynamic.hidden = true;
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (add) add.disabled = !variant.available;
      if (label && add) label.textContent = variant.available ? add.dataset.labelAdd : add.dataset.labelSoldout;
      if (total) total.hidden = !variant.available;
      if (dynamic) dynamic.hidden = !variant.available;
      this.updateTotal();

      if (this.dataset.updateUrl !== 'false') {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        } catch (error) {
          // Some preview contexts do not allow URL updates; selection still works.
        }
      }
      if (variant.featured_media) this.showMedia(variant.featured_media.id);
      this.refreshPrice(variant.id);
    }

    // Marks a value as sold out when no available variant has it together
    // with the values currently selected in the other option groups.
    updateSoldOutPills() {
      const selected = this.selectedOptions();
      this.optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('[data-tt-option]').forEach((input) => {
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every((value, i) =>
                i === groupIndex ? value === input.value : selected[i] === null || value === selected[i]
              )
          );
          const pill = input.nextElementSibling;
          if (pill) pill.classList.toggle('tt-pill--soldout', !available);
        });
      });
    }

    // Price markup comes from Liquid through the Section Rendering API, so
    // currency formatting and sale logic stay in one place.
    refreshPrice(variantId) {
      const targets = Array.from(this.querySelectorAll('[data-tt-price]'));
      if (!targets.length || !this.dataset.url) return;

      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.dataset.section);
      const request = (this.priceRequest = Symbol('price'));

      fetch(url.toString())
        .then((response) => response.text())
        .then((html) => {
          if (request !== this.priceRequest) return;
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-tt-price]');
          targets.forEach((target, i) => {
            if (fresh[i]) target.innerHTML = fresh[i].innerHTML;
          });
        })
        .catch(() => {});
    }

    /* Quantity and total */

    get quantity() {
      const input = this.querySelector('[data-tt-qty-input]');
      const value = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    onQuantityChange(step) {
      const input = this.querySelector('[data-tt-qty-input]');
      if (!input) return;
      const max = parseInt(input.max, 10);
      let next = this.quantity + step;
      if (next < 1) next = 1;
      if (Number.isFinite(max) && next > max) next = max;
      input.value = next;
      this.updateTotal();
    }

    updateTotal() {
      const total = this.querySelector('[data-tt-total]');
      if (!total || !this.variant) return;
      total.textContent = formatMoney(this.variant.price * this.quantity, this.dataset.moneyFormat);
    }

    /* Delivery estimate */

    updateDelivery() {
      this.querySelectorAll('[data-tt-delivery]').forEach((row) => {
        const output = row.querySelector('[data-tt-delivery-date]');
        let remaining = parseInt(row.dataset.days, 10);
        if (!output || !Number.isFinite(remaining) || remaining < 1) return;

        const skip = row.dataset.skip;
        const date = new Date();
        while (remaining > 0) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (skip === 'sun' && day === 0) continue;
          if (skip === 'sat_sun' && (day === 0 || day === 6)) continue;
          remaining -= 1;
        }

        const options = { weekday: 'short', day: 'numeric', month: 'short' };
        const shopify = window.Shopify || {};
        const locale = shopify.locale ? [shopify.locale, shopify.country].filter(Boolean).join('-') : undefined;
        try {
          output.textContent = date.toLocaleDateString(locale, options);
        } catch (error) {
          output.textContent = date.toLocaleDateString(undefined, options);
        }
      });
    }

    /* Add to cart */

    setStatus(message, options = {}) {
      const status = this.querySelector('[data-tt-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
      status.classList.toggle('tt-form__status--error', !!options.error);
      if (message && options.linkText && options.linkUrl) {
        const link = document.createElement('a');
        link.href = options.linkUrl;
        link.textContent = options.linkText;
        status.append(' ', link);
      }
    }

    async onSubmit(event) {
      const add = this.querySelector('[data-tt-add]');
      if (!add) return;
      event.preventDefault();
      if (add.disabled || add.getAttribute('aria-busy') === 'true') return;

      add.setAttribute('aria-busy', 'true');
      this.setStatus('');

      try {
        const result = await addToCart(new FormData(this.form), this.dataset.cartAddUrl);
        if (!result.openedCart) {
          if (this.dataset.afterAdd === 'cart') {
            window.location.assign(this.dataset.cartUrl);
            return;
          }
          this.setStatus(add.dataset.labelAdded, {
            linkText: add.dataset.labelViewCart,
            linkUrl: this.dataset.cartUrl,
          });
        }
      } catch (error) {
        if (error.fromCart) {
          this.setStatus(error.message, { error: true });
        } else {
          // Network or parsing problem: fall back to a normal form post.
          this.form.submit();
        }
      } finally {
        add.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('tt-product')) customElements.define('tt-product', TTProduct);

  /* ---------- Recommendations ---------- */

  class TTRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('[data-tt-recs-content]') || !this.dataset.url) return;

      const load = () => {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((html) => {
            const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('tt-recommendations');
            if (fresh && fresh.querySelector('[data-tt-recs-content]')) {
              this.innerHTML = fresh.innerHTML;
              this.classList.remove('tt-recs--pending');
              updateArrows(this);
            } else {
              this.hidden = true;
            }
          })
          .catch(() => {
            this.hidden = true;
          });
      };

      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(this);
    }
  }

  if (!customElements.get('tt-recommendations')) customElements.define('tt-recommendations', TTRecommendations);

  /* ---------- Carousels with arrow buttons ---------- */

  function updateArrows(scope) {
    const scroller = scope.matches('[data-tt-scroller]') ? scope : scope.querySelector('[data-tt-scroller]');
    if (!scroller) return;
    const track = scroller.querySelector('[data-tt-scroll-track]');
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    scroller.querySelectorAll('[data-tt-scroll]').forEach((button) => {
      button.disabled = Number(button.dataset.ttScroll) < 0 ? track.scrollLeft <= 2 : track.scrollLeft >= max;
    });
  }

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-tt-scroll]');
    if (arrow) {
      const scroller = arrow.closest('[data-tt-scroller]');
      const track = scroller && scroller.querySelector('[data-tt-scroll-track]');
      if (track) {
        track.scrollBy({ left: Number(arrow.dataset.ttScroll) * track.clientWidth * 0.9, behavior: 'smooth' });
      }
    }

    // Size guide and other dialogs
    const opener = event.target.closest('[data-tt-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.ttDialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
      }
    }
    const closer = event.target.closest('[data-tt-dialog-close]');
    if (closer) closer.closest('dialog').close();
    if (event.target instanceof HTMLDialogElement && event.target.matches('.tt-dialog')) event.target.close();

    // Close any open quick-add panel when clicking elsewhere
    document.querySelectorAll('.tt-quick[open]').forEach((quick) => {
      if (!quick.contains(event.target)) quick.removeAttribute('open');
    });
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (!(track instanceof Element) || !track.matches('[data-tt-scroll-track]')) return;
      const scroller = track.closest('[data-tt-scroller]');
      if (scroller) updateArrows(scroller);
    },
    { capture: true, passive: true }
  );

  const initArrows = () => document.querySelectorAll('[data-tt-scroller]').forEach(updateArrows);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initArrows);
  else initArrows();
  window.addEventListener('resize', initArrows);
  document.addEventListener('shopify:section:load', initArrows);

  /* ---------- Quick add on product cards ---------- */

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-tt-quick-form]')) return;
    event.preventDefault();

    const button = event.submitter;
    const formData = new FormData(form);
    if (button && button.name) formData.set(button.name, button.value);
    if (!formData.get('id') || (button && button.getAttribute('aria-busy') === 'true')) return;

    const label = form.querySelector('[data-tt-quick-label]');
    if (button) button.setAttribute('aria-busy', 'true');

    try {
      const result = await addToCart(formData, form.dataset.cartAddUrl);
      if (!result.openedCart && form.dataset.afterAdd === 'cart') {
        window.location.assign(form.dataset.cartUrl);
        return;
      }
      if (label && form.dataset.labelAdded) label.textContent = form.dataset.labelAdded;
      const quick = form.closest('.tt-quick');
      if (quick) window.setTimeout(() => quick.removeAttribute('open'), 1200);
    } catch (error) {
      if (error.fromCart) {
        if (label) label.textContent = error.message;
      } else {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'id';
        hidden.value = formData.get('id');
        form.append(hidden);
        form.submit();
      }
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  });
})();
